import {Modal} from "../../../../../components/modal/modal.jsx";
import {FormProvider, useForm} from "react-hook-form";
import ModalBody from "../../../../../components/modal/modal-body.jsx";
import ModalFooter from "../../../../../components/modal/modal-footer.jsx";
import Button from "../../../../../components/button/button.jsx";
import React, {useEffect, useMemo, useState} from "react";
import {ChangeRouteLaneList, LanesFormMenu} from "./components/lanes-form-ui.jsx";
import PclRouteForm from "./components/pcl-route-form.jsx";
import {
  buildFormData, handleDriverNameOnUrl,
  lanesCapturePlace,
  lanesLane,
  lanesRoute, MODELS,
  MODELS_WITH_EXTRA_SETTINGS, smartcaptureDriver
} from "../../utils/lanes-form-utils.js";
import ModalHeader from "./components/modal-header.jsx";
import LaneForm from "./components/lane-form.jsx";
import CftvForm from "./components/cftv-form.jsx";
import Camera from "./components/camera.jsx";
import Settings from "./components/settings.jsx";
import MySwal from "../../../../../components/swal/my-swal.jsx";
import {FaCopy} from "react-icons/fa";
import lanesService from "../../../../../services/web/lanes-service.js";
import Swal from "sweetalert2";
import {toast} from "react-toastify";
import Alert from "../../../../../components/alert/alert.jsx";
import {activateActionFeedback, resetActionFeedback} from "../../../../../utils/action-feedback.js";
import projectUtils from "../../../../../utils/project-utils.js";

const API_URL = import.meta.env.VITE_API_URL;

const formObj = {
  lane: lanesLane,
  capturePlace: lanesCapturePlace,
  capturePlaceSnapshot: null,
  route: lanesRoute,
  routeSnapshot: null,
  integrations: [],
  smartcaptureDriver: smartcaptureDriver,
  isNewCapturePlace: false,
  isNewRoute: false,
  forwardBackFirstSelected: "",
}
const LanesForm = ({show, setShow, setId, maintenanceGroup, data, ftpUsers, mutation, forceInactiveOnCreate = false}) => {
  const [dataInfo, setDataInfo] = useState(null);
  const methods = useForm({
    defaultValues: formObj
  });

  const capturePlaceDescription = methods.watch("capturePlace.description");
  const routeDescription = methods.watch("route.description");
  const routeRegionType = methods.watch("route.regionType");
  const laneType = methods.watch("lane.type");
  const laneKey = methods.watch("lane.laneKey");
  const code = methods.watch("lane.code");
  const laneDescription = methods.watch("lane.description");
  const setup = methods.watch("smartcaptureDriver.setup");
  const backModel = methods.watch("smartcaptureDriver.back.model");
  const forwardModel = methods.watch("smartcaptureDriver.forward.model");
  const forward = methods.watch("smartcaptureDriver.forward");
  const back = methods.watch("smartcaptureDriver.back") ?? null;
  const mobile = methods.watch("capturePlace.mobile") ?? null;

  const forwardEndPoint =
    (laneKey && code && forward?.driver && forward?.model_details?.ipAddress && forward?.model_details?.portNo)
      ? `http://${forward?.model_details?.ipAddress}:${forward?.model_details?.portNo}/smartcapture/receiver/${handleDriverNameOnUrl(forward?.driver, forwardModel)}/${code}/${laneKey}`
      : ""
  const backEndPoint = (laneKey && code && back?.driver && back?.model_details?.ipAddress && back?.model_details?.portNo)
    ? `http://${back?.model_details?.ipAddress}:${back?.model_details?.portNo}/smartcapture/receiver/${handleDriverNameOnUrl(back?.driver, backModel)}/${code}/${laneKey}${setup === "forwardback" ? "/back" : ""}`
    : ""

  const [currentTab, setCurrentTab] = useState("pcl-route");
  const activatedMenus = useMemo(() => {
    let basicOptions = ["pcl-route", "lane", "cftv"];
    if(laneType === "INTERNAL_SMARTCAPTURE") {
      basicOptions = [...basicOptions, "camera"];
    }
    if(MODELS_WITH_EXTRA_SETTINGS.includes(backModel)) {
      basicOptions = [...basicOptions, "camera-settings-back"];
    }
    if(MODELS_WITH_EXTRA_SETTINGS.includes(forwardModel)) {
      basicOptions = [...basicOptions, "camera-settings-forward"];
    }
    return basicOptions;
  }, [laneType, backModel, forwardModel]);

  useEffect(() => {
    if(activatedMenus.includes(currentTab)) return;
    setCurrentTab(activatedMenus.includes("camera") ? "camera" : "pcl-route");
  }, [activatedMenus, currentTab]);

  const mandatoryFields = useMemo(() => {
    const base = [
      {value: "capturePlace.description", label: "Nome", origin: "capturePlace"},
      {value: "lane.type", label: "Tipo de dispositivo", origin: "lane"},
      {value: "lane.archiver", label: "Archiver", origin: "lane"},
      {value: "lane.ocr", label: "OCR", origin: "lane"},
      {value: "lane.images", label: "Imagens", origin: "lane"},
    ];
    if(!mobile || laneType === "INTERNAL_SMARTCAPTURE"){
      base.push({value: "lane.noctime", label: "Offline (minutos)", origin: "lane"});
    }
    if(!mobile){
      base.push(
        {value: "capturePlace.address", label: "Logradouro", origin: "capturePlace"},
        {value: "capturePlace.city", label: "Município", origin: "capturePlace"},
        {value: "capturePlace.latitude", label: "Latitude", origin: "capturePlace"},
        {value: "capturePlace.longitude", label: "Longitude", origin: "capturePlace"},
        {value: "route.description", label: "Nome", origin: "route"},
        {value: "route.regionType", label: "Tipo", origin: "route"},
        );
    }
    if(laneType === "INTERNAL_SMARTCAPTURE"){
      base.push(
        {value: "smartcaptureDriver.setup", label: "Tipo de instalação", origin: "lane"},
      );
    }
    if(setup){
      if(setup.includes("back")){
        const fields = [
          {value: `smartcaptureDriver.back.driver`, label: "Fabricante/Driver" , origin: setup === "back" ? "lane" : "back"},
          {value: `smartcaptureDriver.back.model`, label: "Modelo" , origin: setup === "back" ? "lane" : "back"},
        ]
        base.push(...fields)
      }
      if(setup.includes("forward")){
        const fields = [
          {value: `smartcaptureDriver.forward.driver`, label: "Fabricante/Driver" , origin: setup === "forward" ? "lane" : "forward"},
          {value: `smartcaptureDriver.forward.model`, label: "Modelo" , origin: setup === "forward" ? "lane" : "forward"},
        ]
        base.push(...fields)
      }
    }

    if(backModel){
        const model = MODELS.find(el => el.value === backModel && (setup === "forwardback" ? el.onlyForwardBack : !el.onlyForwardBack));
        const fields = (model?.requiredInput ?? []).map(el => {
          if(!back.disableConnection && ["cameraip", "port", "model_details.user", "model_details.password"].includes(el.value)){
            return null
          } else {
            return {value: `smartcaptureDriver.back.${el.value}`, label: el.label, origin: setup === "back" ? "lane" : "back"}
          }
        }).filter(Boolean);
        base.push(...fields,);
    }
    if(forwardModel){
        const model = MODELS.find(el => el.value === forwardModel && (setup === "forwardback" ? el.onlyForwardBack : !el.onlyForwardBack));
        const fields = (model?.requiredInput ?? []).map(el => {
          if(!forward.disableConnection && ["cameraip", "port", "model_details.user", "model_details.password"].includes(el.value)){
            return null
          } else {
            return {value: `smartcaptureDriver.forward.${el.value}`,label: el.label,origin: setup === "forward" ? "lane" : "forward"};
          }
        }).filter(Boolean);

        base.push(...fields);
    }
    return base;
  }, [mobile, laneType, backModel, forwardModel, setup, forward?.disableConnection, back?.disableConnection])


  // Para resetar o modal:
  useEffect(() => {
    if (show) return;

    const reset = setTimeout(() => {
      setId(null);
      setDataInfo(null);
      methods.reset(formObj);
      setCurrentTab("pcl-route");
    }, 300);

    return () => clearTimeout(reset);
  }, [show]);

  useEffect(() => {
    if(!show || data) return;

    methods.setValue("lane.status", !forceInactiveOnCreate);
    if (forceInactiveOnCreate) {
      methods.setValue("lane.showlane", false);
    }
  }, [show, data, forceInactiveOnCreate]);

  useEffect(() => {
    if(!data || !show) return;
    const {smartcaptureDriver: originDriver, capturePlace, route, archiver, lastPassageInfo, createdAt, firstReceptionDate, code, status,
      description, type, smartcapturePin, maintenanceGroupsId, observation, noctime, ocr, images, rumination, adr, classification,
      showlane, restricted, laneKey
    } = data;

    const lane = {
      code, status, description, type, smartcapturePin, maintenanceGroupsId, observation, noctime, ocr, images, rumination, adr,
      classification, showlane, restricted, laneKey, archiver: archiver.id
    }

    const smartcaptureDriver = structuredClone(originDriver);

    let smartCaptureCopy = null;
    if(smartcaptureDriver){
      ["back", "forward"].forEach(position => {
        const smartLooptype = smartcaptureDriver[position]?.model_details?.smartLooptype;

        if(smartLooptype !== undefined){
          smartcaptureDriver[position].smartLooptype = smartLooptype;
        }
      });

      const {back, forward, setup} = smartcaptureDriver;
      let tempSetup = null;
      let currentSetup = null;

      if(setup === "forwardback"){
        if (back) back.disableConnection = !back.disableConnection;
        if (forward) forward.disableConnection = !forward.disableConnection;
      }
      else if(setup && smartcaptureDriver[setup]){
        smartcaptureDriver[setup].disableConnection = !smartcaptureDriver[setup].disableConnection
      }

      if(back?.driver === "ftp" || forward?.driver === "ftp"){
        tempSetup = setup; // back || forward
        currentSetup = back?.driver || forward?.driver
        smartCaptureCopy = {
          setup: currentSetup,
          ftpSetup: tempSetup,
        };
      }

      if(smartCaptureCopy){
        if(back){
          const { model_details } = back;
          smartCaptureCopy.model_details = model_details;
        }
        if(forward){
          const { model_details } = forward;
          smartCaptureCopy.model_details  = model_details ;
        }
      }
    }

    const integrations = data.cftvIntegrations?.flatMap(el => el.externalConfig || []) || [];

    const uniqueIntegrations = integrations.reduce((acc, current) => {
      const exists = acc.find(item => JSON.stringify(item) === JSON.stringify(current));
      if (!exists) acc.push(current);
      return acc;
    }, []);

    const formattedObj = {
      lane: lane,
      route: route,
      routeSnapshot: structuredClone(route),
      capturePlace: capturePlace,
      capturePlaceSnapshot: structuredClone(capturePlace),
      smartcaptureDriver: smartCaptureCopy ? smartCaptureCopy : smartcaptureDriver,
      integrations: uniqueIntegrations,
    }

    methods.reset(formattedObj)

    setDataInfo({...lastPassageInfo, createdAt: createdAt, firstReceptionDate: firstReceptionDate, archiver: archiver.id, route: structuredClone(route),
      capturePlace: structuredClone(capturePlace), smartcaptureDriver: smartCaptureCopy ? smartCaptureCopy : smartcaptureDriver})
  }, [data, show]);

  const validateCoordinates = (capturePlace) => {
    if (capturePlace.mobile || (!capturePlace.latitude || !capturePlace.longitude)) return true;

    const regexCoordinates = /^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/gi;
    return regexCoordinates.test(`${capturePlace.latitude},${capturePlace.longitude}`);
  };

  const copyEndPoint = async (endPoint) => {
    if (!navigator.clipboard) {
      const textArea = document.createElement("textarea");
      textArea.value = endPoint;

      textArea.style.position = "fixed";
      textArea.style.top = "-9999px";
      textArea.style.left = "-9999px";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        toast.success("Copiado com sucesso");
      } catch (error) {
        toast.error("Erro ao copiar endpoint.");
      } finally {
        document.body.removeChild(textArea);
      }

      return;
    }

    try {
      await navigator.clipboard.writeText(endPoint);
      toast.success("Copiado com sucesso");
    } catch (error) {
      toast.error("Erro ao copiar endpoint.");
    }
  };

  const onSubmit = async (formData) => {
    const get = (obj, path) => {
      return path
        .split(".")
        .reduce((acc, part) => acc?.[part], obj);
    };

    const values = methods.getValues();
    const emptyFields = [];

    if (!validateCoordinates(values.capturePlace)) {
      resetActionFeedback("save");
      MySwal.fire({
        title: "",
        icon: "error",
        text: "Latitude ou longitude inválida.",
        showConfirmButton: true,
        confirmButtonText: "Ok",
        confirmButtonColor: '#063A6C'
      });
      return;
    }

    mandatoryFields.forEach(el => {
      const value = get(values, el.value);
      if(!value){
        emptyFields.push(el)
      }
    })

    if(emptyFields.length > 0) {
      resetActionFeedback("save");
      projectUtils.swalErrorMessage("", "Preencha os campos obrigatórios");
      return;
    }

    if(methods.getValues("lane.code")){
      let hasChangesRoute = false;
      let hasChangesCapturePlace = false;
      if(dataInfo?.route?.code){
        const currentRoute = methods.getValues("route");

        if(dataInfo.route.code === currentRoute?.code){
          const keysToCheck = [
            'description',
            'regionType',
            'procedureImageid',
            'attach',
            'code'
          ];

          hasChangesRoute = keysToCheck.some(key => currentRoute[key] !== dataInfo?.route[key]);
        }
      }

      if(hasChangesRoute){
        const laneCode = methods.getValues("lane.code");
        const routeCode = methods.getValues("route.code")
        const lanes = await lanesService.getLaneListByRoute(routeCode ,laneCode);

        if(lanes.length > 0){
          const { isConfirmed } = await MySwal.fire({
            title: "Atenção",
            icon: "warning",
            html: <ChangeRouteLaneList list={lanes}/>,
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: '#063A6C',
            cancelButtonColor: '#4a5565',
            reverseButtons: true
          });

          if (!isConfirmed) {
            resetActionFeedback("save");
            return;
          }
        }
      }

      if(dataInfo?.capturePlace?.code){
        const currentCapturePlace = methods.getValues("capturePlace");

        if(dataInfo.capturePlace.code === currentCapturePlace?.code){
          const keysToCheck = [
            "description",
            "group",
            "zipcode",
            "address",
            "number",
            "neighborhood",
            "city",
            "latitude",
            "longitude",
            "mobile",
            "code"
          ];

          hasChangesCapturePlace = keysToCheck.some(key => currentCapturePlace[key] !== dataInfo?.capturePlace[key]);
        }
      }

      if(hasChangesCapturePlace){
        const { isConfirmed } = await MySwal.fire({
          title: "Atenção",
          icon: "warning",
          html:
            <div className="flex items-center justify-center flex-col gap-2">
              <span>A alteração do endereço de uma câmera OCR compromete as estatísticas, análises de inteligência e integrações. Se a câmera mudou de lugar, o ideal é <strong>inativar</strong> este cadastro e criar um novo.</span>
              <span>Para continuar, digite <strong>continuar</strong> abaixo:</span>
            </div>,
          showCancelButton: true,
          showConfirmButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          input: "text",
          confirmButtonColor: '#063A6C',
          reverseButtons: true,
          preConfirm(inputValue) {
            if (inputValue.trim() !== "continuar") {
              Swal.showValidationMessage(`Digite corretamente a palavra-chave`);
            }
          }
        });

        if (!isConfirmed) {
          resetActionFeedback("save");
          return;
        }
      }

      const currentBackModel =
        methods.getValues("smartcaptureDriver.back.model") ?? null;

      const currentForwardModel =
        methods.getValues("smartcaptureDriver.forward.model") ?? null;

      const originalBackModel =
        dataInfo?.smartcaptureDriver?.back?.model ?? null;

      const originalForwardModel =
        dataInfo?.smartcaptureDriver?.forward?.model ?? null;

      const hasModelChanges =
        originalBackModel !== currentBackModel ||
        originalForwardModel !== currentForwardModel;

      if(methods.getValues("lane.smartcapturePin")){
        if(hasModelChanges){
          const tempSmartCapturePin = methods.getValues("lane.smartcapturePin");

          formData.lane.smartcapturePin = null;

          activateActionFeedback("save");
          const formDataPayload = buildFormData(formData);
          await mutation.mutateAsync({method: "update", payload: formDataPayload, silent: true});
          await MySwal.fire({
            title: "Atenção",
            icon: "warning",
            text: `O campo "Fabricante/Driver" foi alterado. A câmera foi removida e adicionada novamente ao smartcapture.`,
            showConfirmButton: true,
            confirmButtonText: "OK",
            confirmButtonColor: '#063A6C',
          });
          formData.lane.smartcapturePin = tempSmartCapturePin;
        }
      }

      activateActionFeedback("save");
      const formDataPayload = buildFormData(formData);
      await mutation.mutateAsync({method: "update", payload: formDataPayload});
    } else {
      activateActionFeedback("save");
      const formDataPayload = buildFormData(formData);
      await mutation.mutateAsync({method: "create", payload: formDataPayload});
    }

    setTimeout(() => {
      setId(null);
      setShow(false);
    }, 1000);
  };

  const handleDelete = async () => {
    if(!code) return;

    try {
      const deleteText = `${capturePlaceDescription || ""}${routeDescription ? ` (${code ? `${code} - ` : ""}${routeDescription}${laneDescription ? `: ${laneDescription}` : ""})` : ""}`;

      const { isConfirmed } = await MySwal.fire({
        title: "Atenção",
        icon: "warning",
        text: `Excluir câmera "${deleteText}"?`,
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
        confirmButtonColor: '#063A6C',
        cancelButtonColor: '#c10007',
        reverseButtons: true
      });

      if (!isConfirmed) return;

      await mutation.mutateAsync({method: "delete", payload: code});

      setTimeout(() => setShow(false), 1000);

    } catch (err) {
      console.error(err)
    }
  }

  const renderForm = (currentForm) => {
    switch(currentForm){
      case "pcl-route": return <PclRouteForm dataInfo={dataInfo}/>
      case "lane": return <LaneForm maintenanceGroup={maintenanceGroup} dataInfo={dataInfo} forceInactiveOnCreate={forceInactiveOnCreate && !code}/>
      case "cftv": return <CftvForm/>
      case "camera": return <Camera ftpUsers={ftpUsers}/>
      case "camera-settings-forward": return <Settings setup="forward" dataInfo={dataInfo}/>
      case "camera-settings-back": return <Settings setup="back" dataInfo={dataInfo}/>
      default: return <div className="h-full w-full bg-pink-500 rounded"></div>
    }
  }

  const renderForms = () => activatedMenus.map(menu => (
    <div
      key={menu}
      data-required-tab={menu}
      hidden={currentTab !== menu}
      aria-hidden={currentTab !== menu}
    >
      {renderForm(menu)}
    </div>
  ));

  return(
    <Modal open={show} setOpen={setShow} title={<ModalHeader code={code} capturePlaceDescription={capturePlaceDescription} routeDescription={routeDescription} routeRegionType={routeRegionType} laneDescription={laneDescription}/>} isCloseClick={false} overflow={true}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate className="h-full flex flex-col">
          <ModalBody className={"px-5!"}>
            <div className="flex gap-2 w-full">
              <LanesFormMenu
                activatedMenus={activatedMenus}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                thumbnailSrc={dataInfo?.latestPassageUrl ? API_URL + "/lanes/latest-passage/get?url=" + dataInfo.latestPassageUrl : ""}
                hasChanges={methods.formState.isDirty}
                className="flex-1"
              />
              <div className="flex-4">
                {renderForms()}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            { (forwardEndPoint || backEndPoint) &&
              <div className="flex-1 flex flex-col">
                <span className="font-bold">Endpoint a ser configurado na câmera para envio de imagens:</span>
                { forwardEndPoint &&
                  <div className="flex gap-1 items-center">
                    <span className="font-bold">Dianteira: </span>
                    <span>{forwardEndPoint}</span>
                    <FaCopy className="cursor-pointer" onClick={() => copyEndPoint(forwardEndPoint)}/>
                  </div>
                }
                { backEndPoint &&
                  <div className="flex gap-1 items-center">
                    <span className="font-bold">Traseira: </span>
                    <span>{backEndPoint}</span>
                    <FaCopy className="cursor-pointer" onClick={() => copyEndPoint(backEndPoint)}/>
                  </div>
                }
              </div>
            }
           <div className="flex gap-2 justify-end">
             { !!methods.watch("lane.code") &&
               <>
                 {dataInfo?.firstReceptionDate &&
                   <Alert target="warning" className="flex h-[32px] items-center">Não é possível excluir, pois a câmera possui passagens associadas.</Alert>
                 }
                 <Button type="button" target="danger" disabled={dataInfo?.firstReceptionDate} onClick={handleDelete} actionFeedbackType='delete' deferActionFeedback>Excluir</Button>
               </>
             }
             <div title={setup === "ftp" ? "Tipo de instalação descontinuado." : ""}>
               <Button disabled={setup === "ftp"} actionFeedbackType='save' deferActionFeedback>Salvar</Button>
             </div>
           </div>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default LanesForm;